import { createApi } from '@reduxjs/toolkit/query/react';
import { customBaseQuery } from '@/lib/baseQuery';
import { ApiResponse } from '@/shared/types';
import type { BootstrapAssessmentResponse } from '../types/bootstrap-assessment.type';
import type { CalculationData } from '@/pages/application/credit-analyst/pages/blueprint/types';

export type SnapshotSource = 'purchase' | 'refinance' | 'hubspot' | 'website';

type GetClientSnapshotArgs = {
  source: SnapshotSource;
  sourceId: string;
};

export type AssessmentType = 'PURCHASE' | 'REFINANCE';
export type AssessmentSource = 'GRAVITY' | 'APP';

type AssignCreditAnalystArgs = {
  assessmentId: string;
  creditAnalystId: number;
};

type AssignHubspotDealArgs = {
  assessmentId: string;
  hubspotDealId: string;
};

// ── Credit-analyst document translation ──────────────────────────────────
// Persisted, one row per document (keyed by documentId). Poll the list and
// watch each row's status flip QUEUED → RUNNING → COMPLETED.
export type TranslationStatus = 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED';

/** The translation denotion overlaid onto each document row. */
export interface DocumentTranslation {
  id: string;
  documentId: string; // join key → merge onto the document list
  docType?: string;
  status: TranslationStatus;
  // Stable, non-expiring app route (e.g. /v1/documents/translations/{id}/file),
  // NOT a SAS. Auth-guarded — must be fetched WITH the bearer token (a bare
  // anchor 401s); see useAssessmentTranslations.openTranslatedPdf. Present only
  // when COMPLETED.
  translatedPdfUrl?: string;
  fieldCount: number;
  lowConfidenceCount: number;
  error?: string; // present only when FAILED
  translatedAt?: string | null;
}

export interface TranslateDocumentsAccepted {
  jobId: string;
  translations: DocumentTranslation[]; // initial QUEUED rows
}

/**
 * Render mode for the translated document:
 * - `reconstruct` (default): a clean rebuilt document with translated labels.
 * - `overlay`: translations drawn onto the source scan for cross-referencing.
 */
export type RenderMode = 'reconstruct' | 'overlay';

type TranslateAssessmentDocumentsArgs = {
  assessmentId: string;
  documentIds: string[]; // the DocumentRecord ids the CA selected
  mode?: RenderMode; // defaults to reconstruct server-side
};

/**
 * Partial update payload for `PATCH assessments/:id`
 * (mirrors the backend `UpdateMortgageAssessmentDto`). Only the provided keys
 * are applied — identity/audit fields are not editable.
 */
export type UpdateAssessmentPayload = Partial<{
  status: string;
  sourceStatus: string;
  sourceCurrentStep: string;
  hubspotStatus: string;
  googleSheetStatus: string;
  slackStatus: string;
  fileInviteStatus: string;
  hubspotDealId: string;
  hubspotContactId: string;
  googleSheetId: string;
  assessmentFolderId: string;
  driveFolderId: string;
  preliminaryAssessmentDocumentId: string;
  brokerId: number;
  caId: number;
  reminderOptOut: boolean;
}>;

type UpdateAssessmentArgs = {
  assessmentId: string;
  body: UpdateAssessmentPayload;
};

export type CreateClientDriveFolderResult = {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  driveFolderId?: string;
  folderId?: string;
  folderName?: string;
  folderUrl?: string;
  [key: string]: unknown;
};

type SyncAssessmentArgs = {
  assessmentType: AssessmentType;
  sourceAssessmentId: string;
  skipIfExists?: boolean | '';
};

type GetAssessmentsSummaryArgs = {
  limit?: number;
  page?: number;
  search?: string;
  assessmentType?: AssessmentType;
  source?: AssessmentSource;
  creditAnalystId?: number;
  /** One or more HubSpot deal stage ids; a row matches any of them. */
  dealStage?: string[];
};

export const applicationApi = createApi({
  reducerPath: 'applicationApi',
  baseQuery: customBaseQuery,
  tagTypes: [
    'ClientSnapshot',
    'AssessmentsSummary',
    'BootstrapAssessment',
    'Calculation',
    'AssessmentTranslations',
  ],
  endpoints: (builder) => ({
    getAssessmentsSummary: builder.query<
      ApiResponse<any>,
      GetAssessmentsSummaryArgs | void
    >({
      query: (args) => ({
        url: 'assessments/summary',
        params: {
          limit: args?.limit ?? 20,
          page: args?.page ?? 1,
          ...(args?.search && { search: args.search }),
          ...(args?.assessmentType && { assessmentType: args.assessmentType }),
          ...(args?.source && { source: args.source }),
          ...(args?.creditAnalystId && {
            creditAnalystId: args.creditAnalystId,
          }),
          ...(args?.dealStage?.length && {
            dealStage: args.dealStage.join(','),
          }),
        },
      }),
      providesTags: ['AssessmentsSummary'],
    }),
    getClientSnapshot: builder.query<ApiResponse<any>, GetClientSnapshotArgs>({
      query: ({ source, sourceId }) => ({
        url: `v1/online-assessment/application/snapshot/${source}/${sourceId}`,
        method: 'GET',
      }),

      providesTags: (result, error, { source, sourceId }) => [
        { type: 'ClientSnapshot', id: `${source}:${sourceId}` },
      ],
    }),
    getBootstrapAssessment: builder.query<
      ApiResponse<any>,
      { assessmentType: string; assessmentId: string }
    >({
      query: ({ assessmentType, assessmentId }) =>
        `/mortgage-assessment-bootstrap/${assessmentType}/${assessmentId}`,
    }),
    getBootstrapAssessmentById: builder.query<
      ApiResponse<BootstrapAssessmentResponse>,
      { assessmentId: string }
    >({
      query: ({ assessmentId }) =>
        `mortgage-assessment-bootstrap/${assessmentId}`,
      providesTags: (_result, _error, { assessmentId }) => [
        { type: 'BootstrapAssessment', id: assessmentId },
      ],
    }),
    getCalculationByAssessmentId: builder.query<
      ApiResponse<CalculationData>,
      { assessmentId: string }
    >({
      query: ({ assessmentId }) => `calculations/${assessmentId}`,
      providesTags: (_result, _error, { assessmentId }) => [
        { type: 'Calculation', id: assessmentId },
      ],
    }),
    assignCreditAnalyst: builder.mutation<
      ApiResponse<unknown>,
      AssignCreditAnalystArgs
    >({
      query: (body) => ({
        url: 'assessments/assign-credit-analyst',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { assessmentId }) => [
        { type: 'BootstrapAssessment', id: assessmentId },
        'AssessmentsSummary',
      ],
    }),
    assignHubspotDeal: builder.mutation<
      ApiResponse<unknown>,
      AssignHubspotDealArgs
    >({
      query: (body) => ({
        url: 'assessments/assign-hubspot-deal',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { assessmentId }) => [
        { type: 'BootstrapAssessment', id: assessmentId },
        'AssessmentsSummary',
      ],
    }),
    updateAssessment: builder.mutation<
      ApiResponse<unknown>,
      UpdateAssessmentArgs
    >({
      query: ({ assessmentId, body }) => ({
        url: `assessments/${assessmentId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { assessmentId }) => [
        { type: 'BootstrapAssessment', id: assessmentId },
        'AssessmentsSummary',
      ],
    }),
    createClientDriveFolder: builder.mutation<
      ApiResponse<CreateClientDriveFolderResult>,
      { assessmentId: string }
    >({
      query: ({ assessmentId }) => ({
        url: `assessments/${assessmentId}/create-drive-folder`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { assessmentId }) => [
        { type: 'BootstrapAssessment', id: assessmentId },
        'AssessmentsSummary',
      ],
    }),
    // Translate the selected documents (runs in the background). Returns the
    // job handle plus the initial QUEUED rows; invalidate the list so the UI
    // starts polling immediately.
    translateAssessmentDocuments: builder.mutation<
      TranslateDocumentsAccepted,
      TranslateAssessmentDocumentsArgs
    >({
      query: ({ assessmentId, documentIds, mode }) => ({
        url: `assessments/${assessmentId}/credit-analyst/translate-documents`,
        method: 'POST',
        body: mode ? { documentIds, mode } : { documentIds },
      }),
      transformResponse: (r: ApiResponse<TranslateDocumentsAccepted>) => r.data,
      invalidatesTags: (_result, _error, { assessmentId }) => [
        { type: 'AssessmentTranslations', id: assessmentId },
      ],
    }),
    // List every translation denotion for the assessment. Poll this for
    // progress (pollingInterval on the hook); stop once none are QUEUED/RUNNING.
    listAssessmentTranslations: builder.query<DocumentTranslation[], string>({
      query: (assessmentId) => ({
        url: `assessments/${assessmentId}/credit-analyst/translations`,
        method: 'GET',
      }),
      transformResponse: (r: ApiResponse<DocumentTranslation[]>) => r.data,
      providesTags: (_result, _error, assessmentId) => [
        { type: 'AssessmentTranslations', id: assessmentId },
      ],
    }),
    syncAssessment: builder.mutation<ApiResponse<unknown>, SyncAssessmentArgs>({
      query: ({ assessmentType, sourceAssessmentId, skipIfExists = '' }) => ({
        url: `v1/client-portal/sync/assessment/${assessmentType}/${sourceAssessmentId}`,
        method: 'POST',
        params: { skipIfExists },
      }),
      invalidatesTags: ['AssessmentsSummary'],
    }),
  }),
});

export const {
  useGetClientSnapshotQuery,
  useGetBootstrapAssessmentQuery,
  useGetBootstrapAssessmentByIdQuery,
  useGetCalculationByAssessmentIdQuery,
  useGetAssessmentsSummaryQuery,
  useAssignCreditAnalystMutation,
  useAssignHubspotDealMutation,
  useUpdateAssessmentMutation,
  useCreateClientDriveFolderMutation,
  useSyncAssessmentMutation,
  useTranslateAssessmentDocumentsMutation,
  useListAssessmentTranslationsQuery,
} = applicationApi;
